/**
 * Cart drawer upsell — variant option syncing.
 *
 * Adding to cart is handled by the theme's own <product-form-component>, which already
 * uses the locale-aware cart route, requests every cart section, dispatches CartAddEvent
 * and announces the result. This script only keeps each upsell card's hidden variant
 * input, price and button state in sync with the shopper's option selection.
 *
 * Prices come from Liquid pre-formatted with the `money` filter, so nothing here has to
 * format currency (Shopify.formatMoney is not available on this storefront).
 *
 * Loaded at page level with a delegated listener, so it survives any number of
 * morphSection re-renders of the cart drawer without re-registering.
 */
(function () {
  if (window.cartUpsellVariantSync) return;
  window.cartUpsellVariantSync = true;

  const CARD_SELECTOR = '[data-upsell-card]';

  /**
   * @typedef {Object} UpsellVariant
   * @property {number} id
   * @property {string[]} options
   * @property {boolean} available
   * @property {string} price
   * @property {string|null} compareAtPrice
   */

  /**
   * @param {HTMLElement} card
   * @returns {UpsellVariant[]}
   */
  function readVariants(card) {
    try {
      const parsed = JSON.parse(card.dataset.upsellVariants || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Cart upsell: could not parse variant data', error);
      return [];
    }
  }

  /**
   * Option values the shopper has chosen, indexed by option position. Swatch options are
   * radios, everything else is a select. A group with nothing chosen leaves a hole, which
   * stops any variant from matching.
   *
   * @param {HTMLElement} card
   * @returns {(string|undefined)[]}
   */
  function readSelection(card) {
    /** @type {(string|undefined)[]} */
    const selection = [];

    card.querySelectorAll('[data-upsell-option]').forEach((control) => {
      if (!(control instanceof HTMLElement)) return;
      const index = Number(control.dataset.optionIndex);
      if (!Number.isInteger(index)) return;

      if (control instanceof HTMLSelectElement) {
        selection[index] = control.value;
      } else if (control instanceof HTMLInputElement && control.checked) {
        selection[index] = control.value;
      }
    });

    return selection;
  }

  /**
   * @param {UpsellVariant} variant
   * @param {(string|undefined)[]} selection
   * @returns {boolean}
   */
  function matchesSelection(variant, selection) {
    if (!Array.isArray(variant.options)) return false;
    if (variant.options.length !== selection.length) return false;
    return variant.options.every((value, index) => value === selection[index]);
  }

  /**
   * Flags option values that no available variant can be reached with, given whatever
   * else is currently selected.
   *
   * @param {HTMLElement} card
   * @param {UpsellVariant[]} variants
   * @param {(string|undefined)[]} selection
   */
  function markUnavailableOptions(card, variants, selection) {
    /**
     * @param {number} index
     * @param {string} value
     */
    const reachable = (index, value) => {
      const candidate = selection.slice();
      candidate[index] = value;
      return variants.some((variant) => variant.available && matchesSelection(variant, candidate));
    };

    card.querySelectorAll('[data-upsell-option]').forEach((control) => {
      if (!(control instanceof HTMLElement)) return;
      const index = Number(control.dataset.optionIndex);
      if (!Number.isInteger(index)) return;

      if (control instanceof HTMLSelectElement) {
        Array.from(control.options).forEach((option) => {
          option.disabled = !reachable(index, option.value);
        });
      } else if (control instanceof HTMLInputElement) {
        control.toggleAttribute('data-option-unavailable', !reachable(index, control.value));
      }
    });
  }

  /** @param {HTMLElement} card */
  function syncCard(card) {
    const variants = readVariants(card);
    if (!variants.length) return;

    const selection = readSelection(card);
    const match = variants.find((variant) => matchesSelection(variant, selection)) || null;
    const canAdd = Boolean(match && match.available);

    markUnavailableOptions(card, variants, selection);

    const priceElement = card.querySelector('[data-upsell-price]');
    if (priceElement && match) priceElement.textContent = match.price;

    const compareAtElement = card.querySelector('[data-upsell-compare-at]');
    if (compareAtElement instanceof HTMLElement && match) {
      compareAtElement.textContent = match.compareAtPrice || '';
      compareAtElement.hidden = !match.compareAtPrice;
    }

    const variantInput = card.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = match ? String(match.id) : '';
      variantInput.disabled = !canAdd;
    }

    const button = card.querySelector('.cart-upsell__button');
    if (button instanceof HTMLButtonElement) {
      button.disabled = !canAdd;
    }
  }

  function syncAllCards() {
    document.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      if (card instanceof HTMLElement) syncCard(card);
    });
  }

  /**
   * Points the slider dots at whichever card is currently nearest the start edge.
   *
   * @param {HTMLElement} scroller
   */
  function syncDots(scroller) {
    const strip = scroller.closest('[data-upsell-strip]');
    const dots = strip?.querySelectorAll('[data-upsell-dots] .cart-upsell__dot');
    if (!dots || !dots.length) return;

    const first = scroller.firstElementChild;
    if (!first) return;

    // Card width plus the flex gap between cards.
    const step = first.getBoundingClientRect().width + parseFloat(getComputedStyle(scroller).columnGap || '0');
    if (!step) return;

    // The row stops scrolling before the last card reaches the start edge, so pin the
    // final dot once the end is reached rather than leaving it unreachable.
    const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;
    const index = atEnd
      ? dots.length - 1
      : Math.min(dots.length - 1, Math.max(0, Math.round(scroller.scrollLeft / step)));

    dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === index;
      dot.classList.toggle('is-active', isActive);
      if (isActive) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  function syncAllDots() {
    document.querySelectorAll('[data-upsell-scroller]').forEach((scroller) => {
      if (scroller instanceof HTMLElement) syncDots(scroller);
    });
  }

  document.addEventListener(
    'scroll',
    (event) => {
      const scroller = event.target;
      if (scroller instanceof HTMLElement && scroller.matches('[data-upsell-scroller]')) syncDots(scroller);
    },
    { capture: true, passive: true }
  );

  /* ----------------------------------------------------------------------------------
   * Slider controls
   *
   * The row has its scrollbar hidden, so a plain mouse has no way to move it: a vertical
   * wheel does not scroll a horizontal container and there is no bar to drag. Wheel
   * redirection, click-drag and clickable dots give it back. Touch is left alone —
   * native panning already works there.
   * -------------------------------------------------------------------------------- */

  const DRAG_THRESHOLD = 4;

  /** @param {HTMLElement} scroller */
  function canScroll(scroller) {
    return scroller.scrollWidth - scroller.clientWidth > 1;
  }

  function refreshDraggableState() {
    document.querySelectorAll('[data-upsell-scroller]').forEach((scroller) => {
      if (scroller instanceof HTMLElement) scroller.classList.toggle('is-draggable', canScroll(scroller));
    });
  }

  document.addEventListener(
    'wheel',
    (event) => {
      const target = event.target;
      const scroller = target instanceof Element ? target.closest('[data-upsell-scroller]') : null;
      if (!(scroller instanceof HTMLElement) || !canScroll(scroller)) return;

      // A trackpad's horizontal gesture already works; only redirect vertical intent.
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

      event.preventDefault();
      scroller.scrollLeft += event.deltaY;
    },
    { passive: false }
  );

  /** @type {{scroller: HTMLElement, startX: number, startScroll: number, pointerId: number, moved: boolean} | null} */
  let drag = null;

  document.addEventListener('pointerdown', (event) => {
    // Touch and pen pan natively; hijacking them would double-scroll.
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const scroller = target.closest('[data-upsell-scroller]');
    if (!(scroller instanceof HTMLElement) || !canScroll(scroller)) return;

    // Let real controls keep their own pointer behaviour.
    if (target.closest('button, select, input, label, textarea')) return;

    drag = {
      scroller,
      startX: event.clientX,
      startScroll: scroller.scrollLeft,
      pointerId: event.pointerId,
      moved: false,
    };
  });

  document.addEventListener('pointermove', (event) => {
    if (!drag || event.pointerId !== drag.pointerId) return;

    const distance = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(distance) < DRAG_THRESHOLD) return;

    if (!drag.moved) {
      drag.moved = true;
      drag.scroller.classList.add('is-dragging');
      try {
        drag.scroller.setPointerCapture(drag.pointerId);
      } catch (error) {
        /* capture is a nicety, not a requirement */
      }
    }

    drag.scroller.scrollLeft = drag.startScroll - distance;
  });

  function endDrag() {
    if (!drag) return;

    const { scroller, moved, pointerId } = drag;
    drag = null;

    scroller.classList.remove('is-dragging');
    if (scroller.hasPointerCapture(pointerId)) scroller.releasePointerCapture(pointerId);
    if (!moved) return;

    // Swallow the click that a drag would otherwise fire at a product link.
    /** @param {Event} clickEvent */
    const swallow = (clickEvent) => {
      clickEvent.preventDefault();
      clickEvent.stopPropagation();
    };

    scroller.addEventListener('click', swallow, { capture: true, once: true });
    setTimeout(() => scroller.removeEventListener('click', swallow, { capture: true }), 0);
  }

  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);

  document.addEventListener('click', (event) => {
    const target = event.target;
    const dot = target instanceof Element ? target.closest('[data-upsell-dot]') : null;
    if (!(dot instanceof HTMLElement)) return;

    const strip = dot.closest('[data-upsell-strip]');
    const scroller = strip?.querySelector('[data-upsell-scroller]');
    if (!(scroller instanceof HTMLElement)) return;

    const card = scroller.children[Number(dot.dataset.index) || 0];
    if (!(card instanceof HTMLElement)) return;

    const padding = parseFloat(getComputedStyle(scroller).paddingInlineStart || '0');
    const offset = card.getBoundingClientRect().left - scroller.getBoundingClientRect().left;

    scroller.scrollTo({ left: scroller.scrollLeft + offset - padding, behavior: 'smooth' });
  });

  window.addEventListener('resize', refreshDraggableState);

  // The drawer is a closed <dialog> on page load, so the row measures zero until it
  // opens. Re-measure whenever it actually gets a size.
  const resizeObserver =
    typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => {
          refreshDraggableState();
          syncAllDots();
        })
      : null;

  function observeScrollers() {
    if (!resizeObserver) return;
    resizeObserver.disconnect();
    document.querySelectorAll('[data-upsell-scroller]').forEach((scroller) => resizeObserver.observe(scroller));
  }

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.matches('[data-upsell-option]')) return;

    const card = target.closest(CARD_SELECTOR);
    if (card instanceof HTMLElement) syncCard(card);
  });

  // The drawer is re-rendered from Liquid on every cart change, so re-flag availability
  // once the new markup has landed.
  document.addEventListener('cart:update', () => {
    requestAnimationFrame(() => {
      syncAllCards();
      syncAllDots();
      refreshDraggableState();
      observeScrollers();
    });
  });

  function init() {
    syncAllCards();
    syncAllDots();
    refreshDraggableState();
    observeScrollers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
